import { CSSProperties, useEffect, useState } from "react";
import useScreen from "Hooks/useScreen";
import { clsx, formatNumberToCurrency, getCampaignTypeInfo, styx } from "modules/Utils";
import { useHistory } from "react-router-dom";
import ButtonSymphony from "./ButtonSymphony";
import TagSymphony from "./TagSymphony";
import { useNavbar } from "Hooks/useNavbar";
import {
    AdPlatform,
    CAMPAIGN_STATUS_INFO,
    CampaignStatus,
    CampaignType,
    DEFAULT_CURRENCY,
    FACEBOOK_MINIMUM_10_PERCENT_BUDGET_INCREASE,
    MINIMUM_DAILY_BUDGET_META_PER_DAY_IN_DOLLARS,
    MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS,
    NAV_BAR_WIDTH_COLLAPSED,
    NAV_BAR_WIDTH_EXPANDED,
    PlanType,
    PLAYBOOK_TYPE_INFO,
    PlaybookType
} from 'modules/Const';
import Sty from "modules/Sty";
import Colors from "modules/Colors";
import Icon from "./Icon";
import { CurrencyCodesType, CampaignMetadata } from "modules/Types";
import useCampaignDetails from "Hooks/useCampaignDetails";
import ModalSymphony from "./ModalSymphony";
import InputSymphony from "./InputSymphony";
import Banner from "./Banner";
import dayjs from 'dayjs';
import DatePickerSymphony from "./DatePickerSymphony";
import { toastError, toastSuccess } from "modules/Toast";
import useUser from "Hooks/useUser";
import { FacebookCampaignSubmissionType } from "modules/Campaign";
import { motion } from "framer-motion";
import useBrand from "Hooks/useBrand";
import UpgradeToUnlockCTA from "pages/WebsitePage/components/CustomizeWebsite/components/UpgradeToUnlockCTA";
import CampaignImageSymphony from "./CampaignImageSymphony";

interface StripeSession {
    payment_intent?: string;
}

type ExtendedCampaignMetadata = CampaignMetadata & {
    stripe_session?: StripeSession;
};

interface CampaignDetailsHeaderProps {
    className: string;
    style?: CSSProperties;

    image?: string | null;
    icon?: string | null;
    campaignName: string;
    playbookType: PlaybookType | CampaignType;
    campaignStatus: CampaignStatus;

    onBack?: () => void;

    isPublic?: boolean;
}

// NOTE: component()
const CampaignDetailsHeader = ({
    className,
    style,

    image,
    icon,
    campaignName,
    playbookType,
    campaignStatus,

    onBack,

    isPublic = false,
}: CampaignDetailsHeaderProps) => {
    const history = useHistory();
    const { mStyle, mobileView } = useScreen();
    const { isCollapsed, isHoverSmallSidebar } = useNavbar();
    const { isProBrand } = useBrand();
    const {
        isSymphonyAdmin
    } = useUser()

    const {
        campaignId,
        campaign: campaignDetails,
        campaignAnalyticsData,
        isPausingResumingCampaign,
        isDuplicatingCampaign,
        isRenamingCampaign,
        isEditingCampaignEndDate,
        isRetryingCampaign,
        isEditingCampaignBudget,
        isDownloadingReport,
        isArchivingCampaign,
        pauseCampaign,
        editBudget,
        resumeCampaign,
        duplicateCampaign,
        renameCampaign,
        editEndDate,
        adminRetryCampaign,
        downloadCampaignReport,
        archiveCampaign,
        unarchiveCampaign,
        getCampaign,
        showEditBudgetModal,
        showEditEndDateModal,
        setShowEditBudgetModal,
        setShowEditEndDateModal,
    } = useCampaignDetails();

    const endDate = campaignAnalyticsData?.campaignLength?.endDate;
    const {
        spent: budgetSpent = 0,
    } = campaignAnalyticsData?.budget || {};

    const { campaign_metadata } = campaignDetails || {};
    const {
        v2_campaign_input,
        budget: budgetFromCampaignMetadata,
    } = campaign_metadata || {};
    const { lengthConfig, fbAdAccount, usesExpressSetup } = (v2_campaign_input || {}) as FacebookCampaignSubmissionType;
    const { currency } = fbAdAccount || {};

    // NOTE CONNER: this is a little hacky, we should make this better, basically if it's not tiktok it should be facebook, even though facebook is not in AD_PLATFORM, the backend route uses /facebook
    let adPlatform: AdPlatform | 'facebook' = PLAYBOOK_TYPE_INFO[playbookType as PlaybookType]?.adPlatform || 'facebook';
    if (adPlatform === AdPlatform.META) {
        adPlatform = 'facebook';
    }

    const [isHeaderButtonsMenuOpen, setIsHeaderButtonsMenuOpen] = useState<boolean>(false);

    const [newCampaignNameText, setNewCampaignNameText] = useState<string>(campaignName);
    const [newBudgetText, setNewBudgetText] = useState<string>('');
    const [newEndDateText, setNewEndDateText] = useState<string>('');

    const [showPauseCampaignModal, setShowPauseCampaignModal] = useState<boolean>(false);
    const [showRenameCampaignModal, setShowRenameCampaignModal] = useState<boolean>(false);
    const [showDuplicateCampaignModal, setShowDuplicateCampaignModal] = useState<boolean>(false);
    const [showArchiveCampaignModal, setShowArchiveCampaignModal] = useState<boolean>(false);
    const [minimumDailyBudget, setMinimumDailyBudget] = useState(MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS);
    const [isDateTooLong, setIsDateTooLong] = useState<boolean>(false);
    const [isBudgetTooLow, setIsBudgetTooLow] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<Date>(
        endDate ? dayjs(endDate).toDate() : dayjs().toDate()
    );

    const shouldRenderAdminActions = playbookType !== PlaybookType.GET_PLAYLISTED;


    useEffect(() => {
        if (!newBudgetText) {
            setNewBudgetText(campaignAnalyticsData?.budget?.total?.toString() ?? '');
        }
        if (!newEndDateText) {
            setNewEndDateText(campaignAnalyticsData?.campaignLength?.endDate ?? '');
        }
    }, [campaignAnalyticsData]);

    const getHeaderLeftPosition = () => {
        if (mobileView) {
            return 0;
        }
        if (isCollapsed && !isHoverSmallSidebar) {
            return NAV_BAR_WIDTH_COLLAPSED;
        }
        return NAV_BAR_WIDTH_EXPANDED;
    }

    let isPaused = campaignStatus === CampaignStatus.PAUSED;

    const calculateNewEndDateValidity = () => {
        if (!lengthConfig) {
            return {
                minimumBudget: 0,
                totalBudget: 0,
                isBudgetValid: true
            };
        }

        const startDate = lengthConfig.finalStartDate || lengthConfig.startDate;
        const newCampaignLength = dayjs(selectedDate).diff(startDate, 'days');
        const newMinimumBudgetForNewDate = minimumDailyBudget * newCampaignLength;

        const totalBudget = v2_campaign_input?.budget
            ?? Number(budgetFromCampaignMetadata)
            ?? 0;

        const isBudgetValid = totalBudget >= newMinimumBudgetForNewDate;

        if (!isBudgetValid && !isDateTooLong) {
            setIsDateTooLong(true);
        }

        if (isBudgetValid && isDateTooLong) {
            setIsDateTooLong(false);
        }

        return {
            minimumBudget: newMinimumBudgetForNewDate,
            totalBudget,
            isBudgetValid
        };
    }

    const calculateNewBudgetValidity = () => {
        const newBudget = Number(newBudgetText);
        const totalBudget = v2_campaign_input?.budget
            ?? Number(budgetFromCampaignMetadata)
            ?? 0;

        let suggestedMinimumBudget = (campaignAnalyticsData?.campaignLength.totalDays || 0) * minimumDailyBudget

        if (budgetSpent > suggestedMinimumBudget) {
            suggestedMinimumBudget = budgetSpent * FACEBOOK_MINIMUM_10_PERCENT_BUDGET_INCREASE;
        }

        const isBudgetValid = newBudget >= suggestedMinimumBudget;

        if (!isBudgetValid && !isBudgetTooLow) {
            setIsBudgetTooLow(true);
        }

        if (isBudgetValid && isBudgetTooLow) {
            setIsBudgetTooLow(false);
        }

        return {
            budgetSpent,
            totalBudget,
            suggestedMinimumBudget,
            isBudgetValid,
        }
    }

    const handlePauseResumeCampaign = async () => {
        try {
            let updateText = '';

            if (isPaused) {
                // resume campaign
                await resumeCampaign(campaignId!);
                updateText = 'Your campaign has been resumed.';

            } else {
                // pause campaign
                await pauseCampaign(campaignId!);
                updateText = 'Your campaign has been paused.';
            }

            toastSuccess(updateText);
            setShowPauseCampaignModal(false);
            setIsHeaderButtonsMenuOpen(false);
        } catch (e) {
            let text = 'An error occurred while pausing/resuming your campaign. Please try again.';
            toastError(text);
        } finally {

        }
    }

    const handleDownloadReport = () => {
        console.log('handleDownloadReport');
        if (campaignId) {
            downloadCampaignReport(campaignId);
        }
    }

    const handleRenameCampaign = async () => {
        try {
            await renameCampaign(campaignId!, newCampaignNameText);
            toastSuccess('Campaign renamed successfully');
            setShowRenameCampaignModal(false);
        } catch (error) {
            toastError('An error occurred while renaming your campaign. Please try again.');
        }
    }

    const handleEditBudgetCampaign = async () => {
        try {
            await editBudget(campaignId!, Number(newBudgetText));
            toastSuccess('Budget edited successfully');
            setShowEditBudgetModal(false);
            getCampaign(campaignId!);
        } catch (error) {
            toastError('An error occurred while editing your campaign budget. Please try again.');
        }
    }

    const handleEditEndDateCampaign = async () => {
        try {
            await editEndDate(campaignId!, selectedDate.toISOString());
            toastSuccess('End date edited successfully');
            setShowEditEndDateModal(false)
            getCampaign(campaignId!);
        } catch (error) {
            toastError('An error occurred while editing your campaign end date. Please try again.');
        }
    }

    const handleDuplicateCampaign = async () => {
        if (!campaignId) {
            return
        }

        try {
            const res = await duplicateCampaign(campaignId);

            if (res?.id) {
                // Close the modal first
                setShowDuplicateCampaignModal(false);
                // Navigate to the draft route with the new campaign ID
                const draftRoute = `/marketing2/draft/${res.id}`;
                history.push(draftRoute);
                toastSuccess('Campaign duplicated successfully');
                setIsHeaderButtonsMenuOpen(false);
            } else {
                throw new Error('Failed to duplicate campaign');
            }
        } catch (error) {
            console.error('Error duplicating campaign:', error);
            toastError('Failed to duplicate campaign');
        }
    }

    const handleArchiveCampaign = async () => {
        if (!campaignId) {
            return
        }

        try {
            await archiveCampaign(campaignId);
            toastSuccess('Campaign archived successfully');
            setShowArchiveCampaignModal(false);
            setIsHeaderButtonsMenuOpen(false);
            // Navigate back to campaigns list since archived campaign should be hidden
            if (onBack) {
                onBack();
            }
        } catch (error) {
            console.error('Error archiving campaign:', error);
            toastError('Failed to archive campaign');
        }
    }

    const handleUnarchiveCampaign = async () => {
        if (!campaignId) {
            return
        }

        try {
            await unarchiveCampaign(campaignId);
            toastSuccess('Campaign unarchived successfully');
            setShowArchiveCampaignModal(false);
            setIsHeaderButtonsMenuOpen(false);
            getCampaign(campaignId);
        } catch (error) {
            console.error('Error unarchiving campaign:', error);
            toastError('Failed to unarchive campaign');
        }
    }

    const handleShowPreview = async () => {
        if (campaignId) {
            // try {
            //     const previewLink = {
            //         tiktok: `https://ads.tiktok.com/business/creative/preview?id=${campaignId}`,
            //         facebook: `https://www.facebook.com/ads/library/?id=${campaignId}`
            //     }

            //     if (previewLink[campaignType]) {
            //         // Close the modal first
            //         setShowDuplicateCampaignModal(false);
            //         // Navigate to the draft route with the new campaign ID
            //         const draftRoute = `/marketing2/draft/${response.id}`;
            //         history.push(draftRoute);
            //         toastSuccess('Campaign duplicated successfully');
            //     } else {
            //         throw new Error('Failed to duplicate campaign');
            //     }
            // } catch (error) {
            //     console.error('Error duplicating campaign:', error);
            //     toastError('Failed to duplicate campaign');
            // }
        }
    }

    const renderTitleWithGem = (title: string) => {
        return (
            <div
                className="campaignDetailsHeaderTitle1"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                }}
            >
                <div>{title}</div>
                <Icon
                    className="gemProIconCampaignDetailsHeader"
                    color="purple"
                    icon="gem"
                    size={22}
                />
            </div>
        )
    }

    const renderBudgetErrorMessage = () => {
        const {
            budgetSpent,
            totalBudget,
            suggestedMinimumBudget,
            isBudgetValid,
        } = calculateNewBudgetValidity();

        if (!isBudgetValid) {
            return (
                <Banner
                    className="budgetCampaignDetailsHeaderErrorBanner"
                    color="yellow"
                >
                    Your campaign has already spent {formatNumberToCurrency(budgetSpent)} of {formatNumberToCurrency(totalBudget)} {currency}.  Please enter at least a budget of {formatNumberToCurrency(suggestedMinimumBudget)}.
                </Banner>
            )
        }

        return null;
    }

    const renderDateErrorMessage = () => {
        const {
            isBudgetValid,
            minimumBudget,
            totalBudget,
        } = calculateNewEndDateValidity();

        if (isBudgetValid) {
            return (
                <Banner
                    className="updateEndDateWarning"
                    color="yellow"
                >
                    Heads up: editing your end date may move your campaign into Facebook ads review for a few hours.
                </Banner>
            )
        }

        return (
            <Banner
                className="endDateCampaignDetailsHeaderErrorBanner"
                color="red"
            >
                Your new end date requires a minimum of {formatNumberToCurrency(minimumBudget)}, your current budget is {formatNumberToCurrency(totalBudget)}. Please update your campaign budget first or choose another end date.
            </Banner>
        )
    }

    const handleRetryCampaign = async () => {
        try {
            let platform = adPlatform as string;

            // Use 'playlisting' as the platform for GET_PLAYLISTED campaigns
            if (playbookType === PlaybookType.GET_PLAYLISTED) {
                platform = 'playlisting';
            }

            // Use 'as any' to bypass type checking for the function call
            await adminRetryCampaign(campaignId!, platform as any);
            toastSuccess('Campaign successfully moved to in_review!')
        } catch (e) {
            toastError('There was an error retrying the campaign. Consult the dev team.')
        }
    }
    const renderHeaderButtons = () => {
        if (isPublic) {
            return null;
        }

        let buttonItems: JSX.Element[] = [];

        if (playbookType === PlaybookType.GET_PLAYLISTED) {
            if (isSymphonyAdmin) {
                // Add admin retry button for TO_RETRY and IN_REVIEW statuses
                if (campaignStatus === CampaignStatus.TO_RETRY || campaignStatus === CampaignStatus.IN_REVIEW) {
                    buttonItems.push(
                        <ButtonSymphony
                            key="adminRetryCampaignButton"
                            className="adminRetryCampaign"
                            isLoading={isRetryingCampaign}
                            disabled={isRetryingCampaign}
                            onClick={() => handleRetryCampaign()}
                            variant="outlined"
                            iconLeft="reload"
                        >
                            Admin: Retry Campaign
                        </ButtonSymphony>
                    )
                }

                // Add Stripe payment view button if payment intent exists in stripe_session
                const extMetadata = campaign_metadata as ExtendedCampaignMetadata;
                const paymentIntent = extMetadata?.stripe_session?.payment_intent;
                if (paymentIntent) {
                    const stripeURL = `https://dashboard.stripe.com/payments/${paymentIntent}`;
                    buttonItems.push(
                        <ButtonSymphony
                            key="viewStripePaymentButton"
                            className="viewStripePayment"
                            onClick={() => window.open(stripeURL, '_blank')}
                            variant="outlined"
                            iconLeft="money"
                        >
                            View Stripe Payment
                        </ButtonSymphony>
                    )
                }
            }
            return buttonItems;
        }


        // download report button always
        // buttonItems.push(
        //     <ButtonSymphony
        //         className="downloadReportButton"
        //         onClick={() => { 
        //             handleDownloadReport();
        //         }}
        //         isLoading={isDownloadingReport}
        //         variant="text"
        //         iconLeft="download"
        //     >
        //         Download Report
        //     </ButtonSymphony>
        // )

        buttonItems.push(
            <ButtonSymphony
                key="duplicateCampaignButton"
                className="duplicateCampaignButton"
                isLoading={isDuplicatingCampaign}
                disabled={isDuplicatingCampaign}
                onClick={() => setShowDuplicateCampaignModal(true)}
                variant="text"
                iconLeft="copy"
            >
                Duplicate
            </ButtonSymphony>
        )

        if (adPlatform !== AdPlatform.TIKTOK) {
            buttonItems.push(
                <ButtonSymphony
                    key="renameCampaignButton"
                    className="renameCampaignButton"
                    isLoading={isRenamingCampaign}
                    disabled={isRenamingCampaign}
                    onClick={() => setShowRenameCampaignModal(true)}
                    variant="text"
                    iconLeft="edit"
                >
                    Rename
                </ButtonSymphony>
            )
        }

        // TODO: Remove tiktok validation when express PR is merged to main so it can be for only express campaings
        // https://github.com/f-x-dx/symphony-ui/pull/1633/files#diff-325d9154facebda0da4f94b8f59d98aa635be4c4115271ca8112c273f04472a8R417
        if (adPlatform !== AdPlatform.TIKTOK && !usesExpressSetup && ![
            CampaignStatus.DENIED,
            CampaignStatus.ERROR,
            CampaignStatus.FINISHED,
            CampaignStatus.ARCHIVED,
        ].includes(campaignStatus as any)) {
            buttonItems.push(
                <ButtonSymphony
                    key="pauseCampaignButton"
                    className="pauseCampaignButton"
                    isLoading={isPausingResumingCampaign}
                    disabled={isPausingResumingCampaign}
                    onClick={() => setShowPauseCampaignModal(true)}
                    variant="text"
                    iconLeft={isPaused ? "play" : "pause"}
                >
                    {isPaused ? 'Resume' : 'Pause'}
                </ButtonSymphony>
            )
        }

        if (adPlatform !== AdPlatform.TIKTOK && !usesExpressSetup && [
            CampaignStatus.ACTIVE,
            CampaignStatus.IN_REVIEW,
            CampaignStatus.TO_RETRY,
            CampaignStatus.PAUSED,
            CampaignStatus.WITH_ISSUES,
        ].includes(campaignStatus as any)) {
            buttonItems.push(
                <ButtonSymphony
                    key="editBudgetCampaignButton"
                    className="editBudgetCampaignButton"
                    isLoading={isEditingCampaignBudget}
                    disabled={isEditingCampaignBudget}
                    onClick={() => setShowEditBudgetModal(true)}
                    variant="text"
                    iconLeft="money"
                >
                    Edit Budget
                </ButtonSymphony>
            )

            buttonItems.push(
                <ButtonSymphony
                    key="editEndDateCampaignButton"
                    className="editEndDateCampaignButton"
                    isLoading={isEditingCampaignEndDate}
                    disabled={isEditingCampaignEndDate}
                    onClick={() => setShowEditEndDateModal(true)}
                    variant="text"
                    iconLeft="calendar"
                >
                    Edit End Date
                </ButtonSymphony>
            )
        }


        // Add Archive/Unarchive button for appropriate campaign statuses
        if (campaignStatus === CampaignStatus.ARCHIVED) {
            buttonItems.push(
                <ButtonSymphony
                    key="unarchiveCampaignButton"
                    className="unarchiveCampaignButton"
                    isLoading={isArchivingCampaign}
                    disabled={isArchivingCampaign}
                    onClick={() => setShowArchiveCampaignModal(true)}
                    variant="text"
                    iconLeft="file"
                >
                    Unarchive
                </ButtonSymphony>
            )
        } else if (![
            CampaignStatus.DRAFT,
        ].includes(campaignStatus as any)) {
            buttonItems.push(
                <ButtonSymphony
                    key="archiveCampaignButton"
                    className="archiveCampaignButton"
                    isLoading={isArchivingCampaign}
                    disabled={isArchivingCampaign}
                    onClick={() => setShowArchiveCampaignModal(true)}
                    variant="text"
                    iconLeft="file"
                >
                    Archive
                </ButtonSymphony>
            )
        }

        if (isSymphonyAdmin && campaignStatus === CampaignStatus.TO_RETRY) {
            buttonItems.push(
                <ButtonSymphony
                    key="adminRetryCampaignButton"
                    className="adminRetryCampaign"
                    isLoading={isRetryingCampaign}
                    disabled={isRetryingCampaign}
                    onClick={() => handleRetryCampaign()}
                    variant="outlined"
                    iconLeft="reload"
                >
                    Admin: Retry Campaign
                </ButtonSymphony>
            )
        }

        const headerMenuButtons = (
            <div
                className="campaignDetailsHeaderButtons"
                style={mStyle(sty, 'campaignDetailsHeaderButtons')}
            >
                {buttonItems}
            </div>
        )

        if (mobileView) {
            return (
                <motion.div
                    className="campaignDetailsHeaderButtonsMobileOverlay"
                    style={sty.campaignDetailsHeaderButtonsMobileOverlay}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setIsHeaderButtonsMenuOpen(false);
                        }
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isHeaderButtonsMenuOpen ? 1 : 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {headerMenuButtons}
                </motion.div>
            )
        }

        return headerMenuButtons;
    }

    let canPauseResumeCampaign = usesExpressSetup ? false : isProBrand;

    const renderPauseResumeModal = () => {
        const modalTitle = isPaused ? 'Resume Campaign' : 'Pause Campaign';

        let subtitle = '';
        if (usesExpressSetup) {
            subtitle = "You can't edit this campaign. Run a campaign using your Facebook Login to unlock editing abilities.";
        } else {
            subtitle = isPaused
                ? "Your campaign will be resumed and continue spending until the end date or maximum budget is reached."
                : "Your campaign will be paused and stop spending until you unpause it.";
        }

        const buttonRightText = usesExpressSetup
            ? "Got it"
            : isPaused
                ? "Yes, resume this campaign"
                : "Yes, pause this campaign";

        const onClickButtonRight = usesExpressSetup
            ? () => setShowPauseCampaignModal(false)
            : handlePauseResumeCampaign;

        const buttonRightDisabled = isPausingResumingCampaign;

        return (
            <ModalSymphony
                className="pauseResumeCampaignModal"
                title={modalTitle}
                subtitle={subtitle}
                isOpen={showPauseCampaignModal}
                onClose={() => setShowPauseCampaignModal(false)}
                buttonLeftText="Cancel"
                onClickButtonLeft={() => setShowPauseCampaignModal(false)}
                buttonRightText={buttonRightText}
                onClickButtonRight={onClickButtonRight}
                buttonLeftDisabled={isPausingResumingCampaign}
                buttonRightIsLoading={isPausingResumingCampaign}
                buttonRightDisabled={buttonRightDisabled}
            />
        );
    };

    const renderRenameCampaignModal = () => {
        const canRename = isProBrand;

        return (
            <ModalSymphony
                className="renameCampaignModal"
                title={renderTitleWithGem("Rename Campaign")}
                subtitle={canRename
                    ? `You can rename your campaign to more easily label or identify it. Your campaign name will be shown in the "Marketing" tab and in the campaign details page.`

                    : 'To be able to rename your campaign, please upgrade to Symphony Pro.'}
                isOpen={showRenameCampaignModal}
                onClose={() => setShowRenameCampaignModal(false)}
                buttonLeftText="Cancel"
                onClickButtonLeft={() => setShowRenameCampaignModal(false)}
                buttonRightText={canRename ? "Update campaign" : "Got it"}
                onClickButtonRight={canRename ? handleRenameCampaign : () => setShowRenameCampaignModal(false)}
                buttonRightDisabled={canRename ? newCampaignNameText === campaignName : false}
                buttonRightIsLoading={isRenamingCampaign}
                style={{ gap: 16 }}
            >
                {canRename ? (
                    <>
                        <InputSymphony
                            className="renameCampaignInput"
                            value={newCampaignNameText}
                            label="New campaign name"
                            onChange={(value) => setNewCampaignNameText(value)}
                            placeholder="Enter new campaign name"
                        />
                        <ButtonSymphony
                            className="renameCampaignButton"
                            onClick={() => setNewCampaignNameText(campaignName)}
                            variant="text"
                            disabled={newCampaignNameText === campaignName}
                        >
                            Reset to original name
                        </ButtonSymphony>
                    </>
                ) : !usesExpressSetup ? (
                    <UpgradeToUnlockCTA
                        highlightedPlan={PlanType.PRO}
                        requiresPaidPlan
                        paywallText="Upgrade to Symphony Pro to be able to rename your campaign and get so much more."
                        buttonText="Unlock campaign renaming with Symphony Pro"
                        upgradeSource="Campaign Details - Rename Campaign Modal"
                    />
                ) : null}
            </ModalSymphony>
        );
    };

    const renderEditBudgetModal = () => {
        const canEditBudget = isProBrand;

        return (
            <ModalSymphony
                className="editBudgetCampaignModal"
                title="Edit Budget"
                subtitle="After editing your campaign, it may go into review again while your changes are reflected."
                isOpen={showEditBudgetModal}
                onClose={() => {
                    setNewBudgetText(campaignAnalyticsData?.budget?.total?.toString() ?? '');
                    setShowEditBudgetModal(false);
                }}
                buttonLeftText="Cancel"
                onClickButtonLeft={() => setShowEditBudgetModal(false)}
                buttonRightText={canEditBudget ? "Update campaign" : "Got it"}
                buttonRightIsLoading={isEditingCampaignBudget}
                onClickButtonRight={canEditBudget ? handleEditBudgetCampaign : () => setShowEditBudgetModal(false)}
                buttonRightDisabled={canEditBudget ? isBudgetTooLow : false}
                style={{ gap: 24 }}
            >
                {canEditBudget ? (
                    <>
                        <InputSymphony
                            className="editBudgetInput"
                            value={newBudgetText}
                            label="Edit budget"
                            onChange={(value) => setNewBudgetText(value)}
                            format="money"
                            placeholder="Enter new budget"
                        />
                        {renderBudgetErrorMessage()}
                    </>
                ) : (
                    <UpgradeToUnlockCTA
                        highlightedPlan={PlanType.PRO}
                        requiresPaidPlan
                        paywallText="Upgrade to Symphony Pro to be able to edit your campaign budget and get so much more."
                        buttonText="Unlock budget editing with Symphony Pro"
                        upgradeSource="Campaign Details - Edit Budget Modal"
                    />
                )}
            </ModalSymphony>
        );
    };

    const renderEditEndDateModal = () => {
        const canEditEndDate = isProBrand;

        return (
            <ModalSymphony
                className="editEndDateCampaignModal"
                title="Edit End Date"
                subtitle="After editing your campaign, it may go into review again while your changes are reflected."
                isOpen={showEditEndDateModal}
                onClose={() => setShowEditEndDateModal(false)}
                buttonLeftText="Cancel"
                onClickButtonLeft={() => setShowEditEndDateModal(false)}
                buttonRightText={canEditEndDate ? "Update campaign" : "Got it"}
                onClickButtonRight={canEditEndDate ? handleEditEndDateCampaign : () => setShowEditEndDateModal(false)}
                buttonRightIsLoading={isEditingCampaignEndDate}
                buttonRightDisabled={canEditEndDate ? isDateTooLong : false}
                style={{ gap: 24 }}
            >
                {canEditEndDate ? (
                    <>
                        <DatePickerSymphony
                            className="editEndDatePicker"
                            selected={selectedDate}
                            onChange={(date: Date | null) => {
                                if (!date) return;
                                setSelectedDate(date);
                            }}
                        />
                        {renderDateErrorMessage()}
                    </>
                ) : (
                    <UpgradeToUnlockCTA
                        highlightedPlan={PlanType.PRO}
                        requiresPaidPlan
                        paywallText="Upgrade to Symphony Pro to be able to edit your campaign end date and get so much more."
                        buttonText="Unlock end date editing with Symphony Pro"
                        upgradeSource="Campaign Details - Edit End Date Modal"
                    />
                )}
            </ModalSymphony>
        );
    };

    const renderDuplicateCampaignModal = () => {
        return (
            <ModalSymphony
                className="duplicateCampaignModal"
                title="Duplicate Campaign"
                subtitle="The duplicated campaign will have the same objective, assets, and targeting options."
                isOpen={showDuplicateCampaignModal}
                onClose={() => setShowDuplicateCampaignModal(false)}
                buttonLeftText="Cancel"
                onClickButtonLeft={() => setShowDuplicateCampaignModal(false)}
                buttonLeftDisabled={isDuplicatingCampaign}
                buttonRightText="Duplicate campaign"
                onClickButtonRight={handleDuplicateCampaign}
                buttonRightIsLoading={isDuplicatingCampaign}
                buttonRightDisabled={isDuplicatingCampaign}
            />
        );
    };

    const renderArchiveCampaignModal = () => {
        // Define which statuses require pausing first
        const needsPauseFirst = [
            CampaignStatus.ACTIVE,
            CampaignStatus.IN_REVIEW,
            CampaignStatus.TO_RETRY
        ].includes(campaignStatus);

        const canArchive = [
            CampaignStatus.PAUSED,
            CampaignStatus.FINISHED,
            CampaignStatus.ERROR,
            CampaignStatus.DENIED
        ].includes(campaignStatus);

        let title = 'Archive Campaign';
        let subtitle = '';
        let buttonRightText = 'Got it';
        let onClickButtonRight = () => setShowArchiveCampaignModal(false);
        let buttonRightDisabled = false;

        if (needsPauseFirst) {
            title = 'Archive Campaign';
            subtitle = 'To archive this campaign, please pause it first or wait for it to finish. You can then archive it.';
        } else if (canArchive) {
            title = 'Archive Campaign?';
            subtitle = 'This campaign will be archived and hidden from your campaigns list, but you can still find it by filtering for archived campaigns.';
            buttonRightText = 'Archive Campaign';
            onClickButtonRight = handleArchiveCampaign;
            buttonRightDisabled = isArchivingCampaign;
        } else if (campaignStatus === CampaignStatus.ARCHIVED) {
            title = 'Unarchive Campaign';
            subtitle = 'This campaign will be unarchived and shown in your campaigns list.';
            buttonRightText = 'Unarchive Campaign';
            onClickButtonRight = handleUnarchiveCampaign;
            buttonRightDisabled = isArchivingCampaign;
        } else {
            subtitle = 'This campaign cannot be archived in its current state.';
        }

        return (
            <ModalSymphony
                className="archiveCampaignModal"
                title={title}
                subtitle={subtitle}
                isOpen={showArchiveCampaignModal}
                onClose={() => setShowArchiveCampaignModal(false)}
                buttonLeftText="Cancel"
                onClickButtonLeft={() => setShowArchiveCampaignModal(false)}
                buttonRightText={buttonRightText}
                onClickButtonRight={onClickButtonRight}
                buttonLeftDisabled={isArchivingCampaign}
                buttonRightIsLoading={isArchivingCampaign}
                buttonRightDisabled={buttonRightDisabled}
            />
        );
    };

    // NOTE: render()
    return (
        <>
            <div
                className={clsx("campaignDetailsHeaderElement", className)}
                style={styx(
                    mStyle(sty, 'campaignDetailsHeaderElement'),
                    { left: getHeaderLeftPosition() },
                    { width: `calc(100% - ${getHeaderLeftPosition()}px)` },
                    style
                )}
            >
                <div
                    className="campaignDetailsHeaderLeftContent"
                    style={mStyle(sty, 'campaignDetailsHeaderLeftContent')}
                >
                    {!isPublic &&
                        <Icon
                            className="campaignDetailsHeaderBackButton"
                            onClick={onBack}
                            icon="arrowLeft"
                            size={24}
                            color="black"
                        />
                    }

                    {image &&
                        <CampaignImageSymphony
                            className="campaignCardSymphonyCampaignImage"
                            campaignMetadata={campaign_metadata!}
                            style={mobileView ? {
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                            } : {}}
                        />
                    }

                    {icon &&
                        <Icon
                            className="campaignDetailsHeaderIcon"
                            style={sty.campaignDetailsHeaderIcon}
                            icon={icon}
                            size={40}
                        />
                    }
                    <div className="campaignDetailsHeaderMiddleContent"
                        style={sty.campaignDetailsHeaderMiddleContent}
                    >
                        <div style={{ fontSize: 16, fontWeight: 600 }}>
                            Overview
                        </div>
                        <div className="campaignDetailsHeaderTitle2">
                            {campaignName}
                        </div>
                        <div className="campaignDetailsHeaderTags" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <TagSymphony
                                className="campaignDetailsHeaderTag1"
                                title={getCampaignTypeInfo(playbookType).title}
                                icon={getCampaignTypeInfo(playbookType).icon}
                                type="square"
                                size={mobileView ? 'small' : 'medium'}
                            />
                            {usesExpressSetup &&
                                <TagSymphony
                                    className="campaignDetailsHeaderTag3"
                                    title="Express Setup"
                                    icon="boost"
                                    type="square"
                                    size={mobileView ? 'small' : 'medium'}
                                />
                            }
                            <TagSymphony
                                className="campaignDetailsHeaderTag2"
                                title={CAMPAIGN_STATUS_INFO[campaignStatus].title}
                                color={CAMPAIGN_STATUS_INFO[campaignStatus].color as any}
                                size={mobileView ? 'small' : 'medium'}
                            />
                        </div>
                    </div>
                </div>
                {mobileView && !isPublic ?
                    <Icon
                        className="headerButtonsMenuOpenButton"
                        onClick={() => {
                            setIsHeaderButtonsMenuOpen(!isHeaderButtonsMenuOpen)
                        }}
                        icon="menu"
                        size={28}
                        style={{
                            marginLeft: 'auto',
                        }}
                    />
                    :
                    renderHeaderButtons()
                }
                {isHeaderButtonsMenuOpen && mobileView &&
                    renderHeaderButtons()
                }
            </div>

            {/* MODALS */}
            {shouldRenderAdminActions && (
                <>
                    {renderPauseResumeModal()}
                    {renderRenameCampaignModal()}
                    {renderEditBudgetModal()}
                    {renderEditEndDateModal()}
                    {renderDuplicateCampaignModal()}
                    {renderArchiveCampaignModal()}
                </>
            )}
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    campaignDetailsHeaderElement: {
        position: 'fixed',
        top: 0,
        zIndex: 500,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 24,
        paddingRight: 24,
        gap: 16,
    },
    campaignDetailsHeaderElement_mobile: {
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 16,
        paddingBottom: 16,
        gap: 4,
    },
    campaignDetailsHeaderImage: {
        width: 65,
        height: 65,
        borderRadius: 8,
        objectFit: 'cover',
        backgroundColor: Colors.greyLight,
        flexShrink: 0,
    },
    campaignDetailsHeaderButtons: {
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        alignItems: 'center',
        marginLeft: 'auto',
        border: 'none',
        paddingTop: 0,
        paddingBottom: 0,
        boxShadow: 'none',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    },
    campaignDetailsHeaderButtons_mobile: {
        flexDirection: 'column',
        width: '100%',
        gap: 8,
        borderTop: Sty.border,
        paddingTop: 12,
        paddingBottom: 12,
        boxShadow: Sty.boxShadowBottom,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
    },
    campaignDetailsHeaderButtonsMobileOverlay: {
        position: 'fixed',
        top: 114,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        flexDirection: 'column',
    },
    campaignDetailsHeaderLeftContent: {
        display: 'flex',
        gap: 16,
        alignSelf: 'flex-start',
        justifyContent: 'center',
        alignItems: 'center',
    },
    campaignDetailsHeaderLeftContent_mobile: {
        gap: 6,
    },
    datePickerContainer: {
        width: '100%',
        zIndex: 2000,
    },
    campaignDetailsHeaderMiddleContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    }
}

export default CampaignDetailsHeader;